import React, { useState } from 'react';
import { X, Send, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  subject?: string;
}

const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose, subject = "Readiness Checklist Request" }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: `I would like to request the Investor Readiness Checklist.`
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    
    setTimeout(() => {
      onClose();
      setIsSubmitted(false);
      setFormData({
        name: '',
        email: '',
        company: '',
        message: `I would like to request the Investor Readiness Checklist.`
      });
    }, 3000);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div id="contact-modal-overlay" className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-[#010712]/90 backdrop-blur-sm"
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-lg bg-[#0a1220] rounded-none shadow-none overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/5">
              <div>
                <h3 className="text-xl font-bold text-white tracking-tight">Request Checklist</h3>
                <p className="text-zinc-500 text-xs uppercase tracking-widest mt-1">Final Mandate Strategy</p>
              </div>
              <button 
                onClick={onClose}
                className="text-zinc-500 hover:text-white transition-colors"
                id="close-modal-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form Body */}
            <div className="p-8">
              {isSubmitted ? (
                <div className="py-12 flex flex-col items-center text-center">
                  <CheckCircle2 className="w-16 h-16 text-[#C5A059] mb-6" />
                  <h4 className="text-2xl font-bold text-white mb-2">Request Received</h4>
                  <p className="text-zinc-400">Our team will reach out to you shortly with the Investor Readiness Checklist.</p>
                </div>
              ) : (
                <form id="contact-form" onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2 flex flex-col">
                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#C5A059] mb-1">
                      Full Name
                    </label>
                    <input
                      required
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full bg-[#111d30]/50 border-none rounded-none px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#C5A059]/50 transition-all text-sm h-12"
                      placeholder="Enter your name"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2 flex flex-col">
                      <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#C5A059] mb-1">
                        Business Email
                      </label>
                      <input
                        required
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full bg-[#111d30]/50 border-none rounded-none px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#C5A059]/50 transition-all text-sm h-12"
                        placeholder="email@company.com"
                      />
                    </div>
                    <div className="space-y-2 flex flex-col">
                      <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#C5A059] mb-1">
                        Company
                      </label>
                      <input
                        required
                        type="text"
                        value={formData.company}
                        onChange={(e) => setFormData({...formData, company: e.target.value})}
                        className="w-full bg-[#111d30]/50 border-none rounded-none px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#C5A059]/50 transition-all text-sm h-12"
                        placeholder="Mining Entity Name"
                      />
                    </div>
                  </div>

                  <div className="space-y-2 flex flex-col">
                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#C5A059] mb-1">
                      Message
                    </label>
                    <textarea
                      required
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="w-full bg-[#111d30]/50 border-none rounded-none px-4 py-4 text-white focus:outline-none focus:ring-2 focus:ring-[#C5A059]/50 transition-all text-sm resize-none"
                    />
                  </div>

                  <button
                    disabled={isSubmitting}
                    type="submit"
                    className="w-full h-12 flex items-center justify-center gap-3 bg-[#C5A059] text-zinc-950 rounded-none font-black text-xs uppercase tracking-[0.2em] hover:bg-[#b08c49] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-none border-none cursor-pointer"
                  >
                    {isSubmitting ? (
                      <div className="w-5 h-5 border-2 border-zinc-950/30 border-t-zinc-950 rounded-full animate-spin" />
                    ) : (
                      <>
                        Request Access
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ContactModal;
